import { h, Host } from "@stencil/core";
import dayjs from "dayjs";
import { getUniqueId } from "../../utils/unique-id";
import { initI18nDate } from "../../utils/initI18nDate";
import { DATE_ISO } from "./constants";
import { getCalendarSheet, getInvalidErrorMsg, isDateAfter, isDateBefore, restrictDateToRange } from "./calendar-utils";
import CalendarDay from "./calendar-day";
import { Label } from "../label/label";
const baseClass = "ux-date-picker";
export class DatePicker {
    constructor() {
        this.activeMonth = undefined;
        this.activeYear = undefined;
        this.dateFormat = undefined;
        this.disabled = false;
        this.required = false;
        this.error = false;
        this.errorMsg = null;
        this.firstWeekday = 1;
        this.value = "";
        this.initialValue = this.value;
        this.inputId = undefined;
        this.kind = "default";
        this.label = undefined;
        this.topLabel = undefined;
        this.language = undefined;
        this.maxDate = undefined;
        this.minDate = undefined;
        this.name = "date";
        this.placeholder = undefined;
        this.rangeStart = "";
        this.rangeEnd = "";
        this.resetText = undefined;
        this.todayText = undefined;
        this.isActive = false;
        this.calendarSheet = null;
        this.selectedDay = null;
        this.todayDateISO = null;
    }
    /** The component DOM node */
    domNode;
    /** the value that was used when the component was first initialized */
    // @ts-expect-error Check for typing
    originalValue = null;
    /** Select a date */
    async selectDate(value) {
        const d = dayjs(value);
        if (d.isValid()) {
            this.selectedDay = restrictDateToRange(d, this.minDate, this.maxDate);
            this.activeYear = this.selectedDay.year().valueOf();
            this.activeMonth = this.selectedDay.month().valueOf();
        }
        else {
            this.selectedDay = null;
        }
        this.setValue();
    }
    setValue() {
        this.value = this.selectedDay === null ? "" : this.selectedDay.format(DATE_ISO);
        this.setOriginalValue();
    }
    setOriginalValue() {
        if (this.originalValue === null) {
            this.originalValue = this.value;
        }
    }
    /** Emitted when a date is selected  */
    dateSelected;
    /** Emitted when hovering a calendar day  */
    dateHover;
    setInputId() {
        this.inputId = this.inputId || getUniqueId();
    }
    setLanguage() {
        if (this.language !== undefined && this.language !== "") {
            initI18nDate(this.language)
                .then(() => this.updateCalendar())
                .catch();
        }
    }
    updateValue() {
        if (this.selectedDay !== null && this.value === this.selectedDay.format(DATE_ISO)) {
            return;
        }
        const newDate = dayjs(this.value, DATE_ISO);
        if (newDate.isValid()) {
            this.selectDate(newDate.format(DATE_ISO));
            return;
        }
        this.selectedDay = null;
    }
    setInitialValue() {
        if (this.initialValue !== "") {
            const initialDate = this.dateFormat ? dayjs(this.initialValue, this.dateFormat) : dayjs(this.initialValue);
            if (initialDate.isValid()) {
                this.selectDate(initialDate.format(DATE_ISO));
                return;
            }
        }
        this.selectedDay = null;
    }
    setCalendarOptions() {
        this.updateCalendar();
    }
    setIsActive() {
        if (this.isActive) {
            if (this.selectedDay !== null) {
                this.activeYear = this.selectedDay.year().valueOf();
                this.activeMonth = this.selectedDay.month().valueOf();
            }
            document.addEventListener("click", this.handleClickDoc);
        }
        else {
            document.removeEventListener("click", this.handleClickDoc);
            if (this.required && this.selectedDay === null) {
                this.error = true;
                this.errorMsg = getInvalidErrorMsg(this.domNode) || this.errorMsg;
            }
            else if (this.selectedDay !== null) {
                this.updateInputValue();
            }
        }
    }
    /** DOM node of the input element */
    inputElement = null;
    updateInputValue() {
        const { inputElement, selectedDay, dateFormat } = this;
        if (inputElement === null) {
            return;
        }
        if (selectedDay !== null) {
            inputElement.value = selectedDay.format(dateFormat);
            this.error = false;
        }
        else {
            inputElement.value = "";
        }
    }
    updateCalendar() {
        const { activeYear, activeMonth, firstWeekday, language } = this;
        if (typeof activeYear !== "number" || typeof activeMonth !== "number") {
            this.calendarSheet = null;
            return;
        }
        this.calendarSheet = getCalendarSheet(activeYear, activeMonth, firstWeekday, language);
    }
    reset() {
        this.selectDate(this.originalValue);
        this.dateSelected.emit({
            name: this.name,
            value: this.value
        });
        this.isActive = false;
    }
    showToday() {
        const date = dayjs(this.todayDateISO);
        if (!date.isValid()) {
            return;
        }
        this.activeYear = date.year();
        this.activeMonth = date.month();
    }
    getDayIsDisabled(dateISO) {
        // @ts-expect-error Check for typing
        return isDateAfter(dateISO, this.maxDate) || isDateBefore(dateISO, this.minDate);
    }
    getIsInRange(dateISO) {
        const { rangeStart, rangeEnd } = this;
        if (this.rangeStart === "" && this.rangeEnd === "") {
            return false;
        }
        if (rangeStart !== "" && rangeEnd === "") {
            return isDateAfter(dateISO, rangeStart, true);
        }
        if (rangeEnd !== "" && rangeStart === "") {
            return isDateBefore(dateISO, rangeEnd, true);
        }
        return isDateAfter(dateISO, rangeStart, true) && isDateBefore(dateISO, rangeEnd, true);
    }
    stepActiveMonth = (step) => {
        // @ts-expect-error Check for typing
        let nextMonth = (this.activeMonth += step);
        if (nextMonth > 11) {
            // @ts-expect-error Check for typing
            this.activeYear++;
            nextMonth -= 12;
        }
        else if (nextMonth < 0) {
            // @ts-expect-error Check for typing
            this.activeYear--;
            nextMonth += 12;
        }
        this.activeMonth = nextMonth;
    };
    stepActiveYear = (step) => {
        // @ts-expect-error Check for typing
        this.activeYear += step;
    };
    handleClickField = () => {
        if (this.disabled) {
            return;
        }
        this.isActive = !this.isActive;
    };
    handleSelectDay = (day) => {
        const selectedDay = restrictDateToRange(day, this.minDate, this.maxDate);
        const event = this.dateSelected.emit({
            name: this.name,
            value: selectedDay.format(DATE_ISO)
        });
        if (!event.defaultPrevented) {
            this.selectedDay = selectedDay;
            this.error = false;
        }
        this.isActive = false;
        this.setValue();
    };
    handleHoverDay = (day) => {
        this.dateHover.emit({
            name: this.name,
            value: day.format(DATE_ISO)
        });
    };
    handleClickDoc = (event) => {
        if (!this.isActive) {
            return;
        }
        if (event.target.closest(baseClass) === this.domNode) {
            return;
        }
        this.isActive = false;
    };
    handleInputChange = (event) => {
        const value = event.target.value;
        const d = dayjs(value, this.dateFormat);
        if (d.isValid()) {
            this.selectDate(d.format(DATE_ISO));
            this.error = false;
        }
        else if (value.length > 0 || this.required) {
            this.error = true;
            this.errorMsg = getInvalidErrorMsg(this.domNode) || this.errorMsg;
        }
    };
    componentWillLoad() {
        const today = dayjs();
        this.todayDateISO = today.format(DATE_ISO);
        if (typeof this.activeYear !== "number") {
            this.activeYear = today.year();
        }
        if (typeof this.activeMonth !== "number") {
            this.activeMonth = today.month();
        }
        this.setInputId();
        this.setLanguage();
        this.setInitialValue();
        this.updateValue();
        this.setCalendarOptions();
    }
    disconnectedCallback() {
        document.removeEventListener("click", this.handleClickDoc);
    }
    render() {
        const { activeMonth, activeYear, calendarSheet, dateFormat, disabled, error, errorMsg, inputId, isActive, kind, label, topLabel, name, placeholder, required, selectedDay, todayDateISO, todayText, resetText } = this;
        const selectedDateISO = selectedDay !== null ? selectedDay.format(DATE_ISO) : "";
        const selectedDateFormatted = selectedDay !== null ? selectedDay.format(dateFormat) : "";
        const hasFooter = todayText !== undefined || resetText !== undefined;
        const hasTopLabel = topLabel && topLabel.length > 0;
        return (h(Host, { key: '33a5725a7a600fe19bb0a4098eb095d94d05dcf1', class: {
                [baseClass]: true,
                [`${baseClass}--disabled`]: !!disabled,
                [`${baseClass}--invalid`]: !!error,
                [`${baseClass}--kind-default`]: kind === "default",
                [`${baseClass}--kind-form`]: kind === "form"
            } }, hasTopLabel && (h(Label, { key: '5cd2c184068c4eae0f596e7e59ba06e2146cf6e4', required: required, label: topLabel, error: error,
            // @ts-expect-error Check for typing
            errorMsg: errorMsg, htmlFor: inputId, labelClasses: `${baseClass}__label--top` })), h("div", { key: '6a27d1ef53fff3578a7e75f6cf7e0280e0b485c7', class: {
                [`${baseClass}__field-wrapper`]: true,
                "ux-u-shadow": kind === "default"
            } }, h(Label
        // @ts-expect-error Check for typing
        , { key: '23bc5328d90f427fd67d695fc098f86753db532b',
            // @ts-expect-error Check for typing
            required: hasTopLabel ? null : required, label: label,
            // @ts-expect-error Check for typing
            error: hasTopLabel ? null : error,
            // @ts-expect-error Check for typing
            errorMsg: hasTopLabel ? null : errorMsg, htmlFor: inputId }), h("div", { key: '1d166e727bfe74c0bf1286074af066118572e076', class: `${baseClass}__field`, onClick: this.handleClickField }, h("input", { key: '22600885c12aef14af7108f3b9b3d5a15dee6355', class: {
                "ux-input": true,
                "ux-input--error": !!error
            }, disabled: disabled, id: inputId, placeholder: placeholder,
            // @ts-expect-error Check for typing
            ref: (el) => (this.inputElement = el), type: "text", "aria-invalid": error ? "true" : "false", onInput: this.handleInputChange,
            // @ts-expect-error Check for typing
            onBlur: this.handleInputChange, value: selectedDateFormatted, required: required, autocomplete: "off" }), h("input", { key: '1f5c2de9c03d9d93a19c0d07da77be5326497498', type: "hidden", name: name, value: this.value }), h("ux-icon", { key: 'e0e90670760b98afa255dad0e3e106f17ffd7d8a', name: "calendar", weight: "light" }))), isActive && (h("div", { key: '171c8bbe282188be3f2b3216fb6f672387023144', class: `${baseClass}__container` }, h("div", { key: 'fe057240ba2562baaedea0e7fcd9f76012f2c0ae', class: `${baseClass}__header` }, h("button", { key: 'c9e7d37c987be45f647aedebadc1ae389a79d96e', type: "button", onClick: () => this.stepActiveYear(-1) }, h("ux-icon", { key: '2a26e6c9cefe7c0ec2bbc1cb95a87efd9ece3811', name: "chevron-left-double", weight: "bold" })), h("button", { key: '23aa06eb1f7609f7c227a7db815b7d52e99296f3', type: "button", onClick: () => this.stepActiveMonth(-1) }, h("ux-icon", { key: '94bc5268d18be0144260dacde1582ab287a35855', name: "chevron-left", weight: "bold" })), h("span", { key: 'd12a659e61d02a5990ccf8ceba6c0886a2118541', class: `${baseClass}__title` }, calendarSheet.monthName, " ", activeYear), h("button", { key: '5426c6962145eadef6d6854bff48e12d6ba58af5', type: "button", onClick: () => this.stepActiveMonth(1) }, h("ux-icon", { key: 'd0ebe7d6c76420a23c32b1a9e6b6a3e11b7d0c5e', name: "chevron-right", weight: "bold" })), h("button", { key: '3466c100df268d8f70d630a062a85773cad62715', type: "button", onClick: () => this.stepActiveYear(1) }, h("ux-icon", { key: 'd8bb8601c633d7046d6005f3d2241c66442550ff', name: "chevron-right-double", weight: "bold" }))), h("div", { key: '1424c15d3ed932115587586ea52a16bf13687465', class: `${baseClass}__calendar` }, calendarSheet.dayNames.map((dayName) => (h("span", { class: {
                [`${baseClass}__calendar-day`]: true,
                [`${baseClass}__calendar-head`]: true
            } }, dayName))), calendarSheet.days.map((dateISO) => {
            const date = dayjs(dateISO);
            return (h(CalendarDay, { baseClass: baseClass, date: date, isDisabled: this.getDayIsDisabled(dateISO), isInactive: date.month() !== activeMonth, isHighlighted: this.getIsInRange(dateISO), isSelected: dateISO === selectedDateISO, isToday: dateISO === todayDateISO,
                // @ts-expect-error Check for typing
                onSelectDay: this.handleSelectDay, onHoverDay: this.handleHoverDay }));
        })), hasFooter && (h("div", { key: 'c887a4c58ae07004ed7bd7adf70c76cad8593421', class: `${baseClass}__footer` }, resetText && (h("button", { key: '7006fdf3667b36ab26c2e9ecfe85af676fcb70b3', class: "ux-text-link ux-text-link--green", onClick: () => this.reset(), type: "button" }, resetText)), todayText && (h("button", { key: '20d0e084a28b10ad5409dd15b5e726afabe3cfee', class: "ux-text-link ux-text-link--green", onClick: () => this.showToday(), type: "button" }, todayText))))))));
    }
    static get is() { return "ux-date-picker"; }
    static get originalStyleUrls() {
        return {
            "$": ["date-picker.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["date-picker.css"]
        };
    }
    static get properties() {
        return {
            "activeMonth": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The currently active month (0-11)"
                },
                "attribute": "active-month",
                "reflect": false
            },
            "activeYear": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The currently active year (YYYY)"
                },
                "attribute": "active-year",
                "reflect": false
            },
            "dateFormat": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Format of the date value"
                },
                "attribute": "date-format",
                "reflect": false
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Flag that controls if the field is disabled"
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "required": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates if the field is mandatory"
                },
                "attribute": "required",
                "reflect": false,
                "defaultValue": "false"
            },
            "error": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates an invalid date"
                },
                "attribute": "error",
                "reflect": true,
                "defaultValue": "false"
            },
            "errorMsg": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string | null",
                    "resolved": "null | string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Error message that is rendered in invalid state"
                },
                "attribute": "error-msg",
                "reflect": false,
                "defaultValue": "null"
            },
            "firstWeekday": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The weekday the calendar starts with (0=sunday, 1=monday, 6=saturday)"
                },
                "attribute": "first-weekday",
                "reflect": false,
                "defaultValue": "1"
            },
            "value": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Initial date, formatted in YYYY-MM-DD"
                },
                "attribute": "value",
                "reflect": true,
                "defaultValue": "\"\""
            },
            "initialValue": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "deprecated",
                            "text": "Initial date, formatted according dateFormat pattern"
                        }],
                    "text": ""
                },
                "attribute": "initial-value",
                "reflect": false,
                "defaultValue": "this.value"
            },
            "inputId": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Input id attribute"
                },
                "attribute": "input-id",
                "reflect": false
            },
            "kind": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"default\" | \"form\"",
                    "resolved": "\"default\" | \"form\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Controls the appearance of the control"
                },
                "attribute": "kind",
                "reflect": false,
                "defaultValue": "\"default\""
            },
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Label text describing the input field"
                },
                "attribute": "label",
                "reflect": false
            },
            "topLabel": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Only to be used in the standard format, will render the label at the top"
                },
                "attribute": "top-label",
                "reflect": false
            },
            "language": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The language in which texts are displayed"
                },
                "attribute": "language",
                "reflect": false
            },
            "maxDate": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Optionally, the date can be restricted to a maximum value (YYYY-MM-DD)"
                },
                "attribute": "max-date",
                "reflect": false
            },
            "minDate": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Optionally, the date can be restricted to a minimum value (YYYY-MM-DD)"
                },
                "attribute": "min-date",
                "reflect": false
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Input name attribute"
                },
                "attribute": "name",
                "reflect": false,
                "defaultValue": "\"date\""
            },
            "placeholder": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Input placeholder text"
                },
                "attribute": "placeholder",
                "reflect": false
            },
            "rangeStart": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Optional range start date (YYYY-MM-DD)"
                },
                "attribute": "range-start",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "rangeEnd": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Optional range end date (YYYY-MM-DD)"
                },
                "attribute": "range-end",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "resetText": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Label for the reset button"
                },
                "attribute": "reset-text",
                "reflect": false
            },
            "todayText": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Label for the today button"
                },
                "attribute": "today-text",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "isActive": {},
            "calendarSheet": {},
            "selectedDay": {},
            "todayDateISO": {}
        };
    }
    static get events() {
        return [{
                "method": "dateSelected",
                "name": "uxDateSelect",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when a date is selected"
                },
                "complexType": {
                    "original": "{ name: string; value: string }",
                    "resolved": "{ name: string; value: string; }",
                    "references": {}
                }
            }, {
                "method": "dateHover",
                "name": "uxDateHover",
                "bubbles": true,
                "cancelable": false,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when hovering a calendar day"
                },
                "complexType": {
                    "original": "{ name: string; value: string }",
                    "resolved": "{ name: string; value: string; }",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "selectDate": {
                "complexType": {
                    "signature": "(value: string) => Promise<void>",
                    "parameters": [{
                            "name": "value",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Select a date",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "domNode"; }
    static get watchers() {
        return [{
                "propName": "inputId",
                "methodName": "setInputId"
            }, {
                "propName": "language",
                "methodName": "setLanguage"
            }, {
                "propName": "value",
                "methodName": "updateValue"
            }, {
                "propName": "initialValue",
                "methodName": "setInitialValue"
            }, {
                "propName": "activeMonth",
                "methodName": "setCalendarOptions"
            }, {
                "propName": "activeYear",
                "methodName": "setCalendarOptions"
            }, {
                "propName": "firstWeekday",
                "methodName": "setCalendarOptions"
            }, {
                "propName": "minDate",
                "methodName": "setCalendarOptions"
            }, {
                "propName": "maxDate",
                "methodName": "setCalendarOptions"
            }, {
                "propName": "isActive",
                "methodName": "setIsActive"
            }];
    }
}
//# sourceMappingURL=date-picker.js.map
